import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  TextInput,
  ActivityIndicator,
  Alert,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { RootStackNavigationProp, RootStackParamList } from '../../../../types/navigation';
import { endOfLifeWishesService } from '../../../../services/endOfLifeWishesInfo';

import { createLogger } from '../../../../utils/logger';
const logger = createLogger('EndOfLifeWishesInput');

type EndOfLifeWishesInputScreenRouteProp = RouteProp<RootStackParamList, 'EndOfLifeWishesInput'>;

const PREFERENCE_OPTIONS = ['Yes, use if needed', 'Limited time only', 'No, do not use', 'Let my agent decide'];

export const EndOfLifeWishesInputScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<EndOfLifeWishesInputScreenRouteProp>();

  const { endOfLifeWishesData, isEditing } = route.params || {};

  const [ventilatorPreference, setVentilatorPreference] = useState(endOfLifeWishesData?.ventilatorPreference || '');
  const [ventilatorTimeLimit, setVentilatorTimeLimit] = useState(endOfLifeWishesData?.ventilatorTimeLimit || '');
  const [ventilatorNotes, setVentilatorNotes] = useState(endOfLifeWishesData?.ventilatorNotes || '');
  const [feedingTubePreference, setFeedingTubePreference] = useState(endOfLifeWishesData?.feedingTubePreference || '');
  const [feedingTubeTimeLimit, setFeedingTubeTimeLimit] = useState(endOfLifeWishesData?.feedingTubeTimeLimit || '');
  const [feedingTubeNotes, setFeedingTubeNotes] = useState(endOfLifeWishesData?.feedingTubeNotes || '');
  const [painManagementPreference, setPainManagementPreference] = useState(endOfLifeWishesData?.painManagementPreference || '');
  const [painManagementNotes, setPainManagementNotes] = useState(endOfLifeWishesData?.painManagementNotes || '');
  const [ivFluidsPreference, setIvFluidsPreference] = useState(endOfLifeWishesData?.ivFluidsPreference || '');
  const [additionalInstructions, setAdditionalInstructions] = useState(endOfLifeWishesData?.additionalInstructions || '');
  const [familyInformed, setFamilyInformed] = useState(endOfLifeWishesData?.familyInformed || false);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!ventilatorPreference && !feedingTubePreference && !painManagementPreference) {
      Alert.alert('Error', 'Please indicate at least one treatment preference');
      return;
    }

    try {
      setSaving(true);

      const data = {
        ventilatorPreference,
        ventilatorTimeLimit: ventilatorTimeLimit.trim(),
        ventilatorNotes: ventilatorNotes.trim(),
        feedingTubePreference,
        feedingTubeTimeLimit: feedingTubeTimeLimit.trim(),
        feedingTubeNotes: feedingTubeNotes.trim(),
        painManagementPreference,
        painManagementNotes: painManagementNotes.trim(),
        ivFluidsPreference,
        additionalInstructions: additionalInstructions.trim(),
        familyInformed,
      };

      let savedData;
      if (isEditing && endOfLifeWishesData?.id) {
        savedData = await endOfLifeWishesService.updateEndOfLifeWishes(endOfLifeWishesData.id, data);
        const updatedData = savedData?.data || savedData;
        logger.debug('End-of-life wishes updated successfully with ID:', updatedData.id);
        navigation.navigate('EndOfLifeWishesLockUnlock', { endOfLifeWishesId: updatedData.id });
      } else {
        savedData = await endOfLifeWishesService.createEndOfLifeWishes(data);
        const createdData = savedData?.data || savedData;
        logger.debug('End-of-life wishes saved successfully with ID:', createdData.id);
        navigation.navigate('EndOfLifeWishesLockUnlock', { endOfLifeWishesId: createdData.id });
      }
    } catch (error) {
      logger.error('Error saving end-of-life wishes:', error);
      Alert.alert('Error', 'Failed to save end-of-life wishes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const renderPreferenceOptions = (selectedValue: string, onSelect: (value: string) => void) => (
    <View style={styles.optionsContainer}>
      {PREFERENCE_OPTIONS.map((option) => (
        <TouchableOpacity
          key={option}
          style={[styles.optionButton, selectedValue === option && styles.optionSelected]}
          onPress={() => onSelect(option)}
        >
          <Text style={[styles.optionText, selectedValue === option && styles.optionTextSelected]}>{option}</Text>
        </TouchableOpacity>
      ))}
    </View>
  );

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity style={styles.backButton} onPress={() => navigation.goBack()}>
          <Image source={require('../../../../assets/icons/back.png')} style={[styles.backIcon, { tintColor: '#fff' }]} />
        </TouchableOpacity>
      </View>

      <View style={styles.topSection}>
        <Image source={require('../../../../assets/icons/keyimage.png')} style={styles.backgroundKey} />
        <Image source={require('../../../../assets/logo.png')} style={styles.logo} resizeMode="contain" />
        <Text style={styles.title}>Treatment Preferences</Text>
      </View>

      <View style={styles.inputContainer}>
        <ScrollView style={styles.scrollView}>
          <Text style={styles.sectionTitle}>Ventilator/Life Support:</Text>
          <Text style={styles.sectionDescription}>A machine that helps you breathe when you cannot breathe on your own.</Text>
          {renderPreferenceOptions(ventilatorPreference, setVentilatorPreference)}

          {ventilatorPreference === 'Limited time only' && (
            <View style={styles.inputGroup}>
              <Text style={styles.label}>Time Limit:</Text>
              <TextInput
                style={styles.input}
                value={ventilatorTimeLimit}
                onChangeText={setVentilatorTimeLimit}
                placeholder="e.g., 2 weeks, 30 days"
                placeholderTextColor="#A9A9A9"
              />
            </View>
          )}

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Additional Notes (Optional):</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={ventilatorNotes}
              onChangeText={setVentilatorNotes}
              placeholder="Any specific conditions or notes"
              placeholderTextColor="#A9A9A9"
              multiline={true}
              numberOfLines={2}
            />
          </View>

          <Text style={styles.sectionTitle}>Feeding Tube:</Text>
          <Text style={styles.sectionDescription}>A tube to provide nutrition when you cannot eat or drink.</Text>
          {renderPreferenceOptions(feedingTubePreference, setFeedingTubePreference)}

          {feedingTubePreference === 'Limited time only' && (
            <View style={styles.inputGroup}>
              <Text style={styles.label}>Time Limit:</Text>
              <TextInput
                style={styles.input}
                value={feedingTubeTimeLimit}
                onChangeText={setFeedingTubeTimeLimit}
                placeholder="e.g., 2 weeks, 30 days"
                placeholderTextColor="#A9A9A9"
              />
            </View>
          )}

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Additional Notes (Optional):</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={feedingTubeNotes}
              onChangeText={setFeedingTubeNotes}
              placeholder="Any specific conditions or notes"
              placeholderTextColor="#A9A9A9"
              multiline={true}
              numberOfLines={2}
            />
          </View>

          <Text style={styles.sectionTitle}>Pain Management:</Text>
          <Text style={styles.sectionDescription}>Medications to control pain and keep you comfortable.</Text>
          <View style={styles.optionsContainer}>
            {['Full comfort care (may affect alertness)', 'Balance comfort and alertness', 'Minimal medication (stay alert)', 'Let my agent decide'].map((option) => (
              <TouchableOpacity
                key={option}
                style={[styles.optionButton, painManagementPreference === option && styles.optionSelected]}
                onPress={() => setPainManagementPreference(option)}
              >
                <Text style={[styles.optionText, painManagementPreference === option && styles.optionTextSelected]}>{option}</Text>
              </TouchableOpacity>
            ))}
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Pain Management Notes (Optional):</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={painManagementNotes}
              onChangeText={setPainManagementNotes}
              placeholder="Any specific preferences"
              placeholderTextColor="#A9A9A9"
              multiline={true}
              numberOfLines={2}
            />
          </View>

          <Text style={styles.sectionTitle}>IV Fluids:</Text>
          <Text style={styles.sectionDescription}>Fluids given through a vein for hydration.</Text>
          {renderPreferenceOptions(ivFluidsPreference, setIvFluidsPreference)}

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Additional Instructions (Optional):</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={additionalInstructions}
              onChangeText={setAdditionalInstructions}
              placeholder="Any other wishes or instructions"
              placeholderTextColor="#A9A9A9"
              multiline={true}
              numberOfLines={4}
              textAlignVertical="top"
            />
          </View>

          <TouchableOpacity
            style={[styles.checkboxContainer]}
            onPress={() => setFamilyInformed(!familyInformed)}
          >
            <View style={[styles.checkbox, familyInformed && styles.checkboxChecked]}>
              {familyInformed && (
                <Image source={require('../../../../assets/icons/check.png')} style={styles.checkmark} />
              )}
            </View>
            <Text style={styles.checkboxLabel}>My family has been informed of these wishes</Text>
          </TouchableOpacity>

          <View style={styles.submitButtonContainer}>
            <TouchableOpacity
              style={[styles.saveButton, saving && { opacity: 0.7 }]}
              onPress={handleSave}
              disabled={saving}
            >
              {saving ? (
                <ActivityIndicator color="#FFFFFF" />
              ) : (
                <Text style={styles.saveButtonText}>{isEditing ? 'Update' : 'Submit'}</Text>
              )}
            </TouchableOpacity>
          </View>

          <View style={styles.bottomPadding} />
        </ScrollView>
      </View>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#29BFC0' },
  header: { flexDirection: 'row', alignItems: 'center', padding: ms(15), paddingTop: ms(25), position: 'relative' },
  backButton: { position: 'absolute', left: ms(50), padding: ms(15), paddingTop: ms(50), zIndex: 1 },
  backIcon: { width: s(32), height: s(32), tintColor: '#fff' },
  topSection: { alignItems: 'center', paddingVertical: ms(20) },
  backgroundKey: { position: 'absolute', width: '85%', height: '85%', opacity: 0.1 },
  logo: { width: s(125), height: s(125), marginBottom: ms(16) },
  title: { fontSize: ms(24, 0.3), fontWeight: 'bold', color: '#FFFFFF', marginBottom: ms(8), paddingHorizontal: ms(30), textAlign: 'center' },
  inputContainer: { flex: 1, backgroundColor: '#F3F6F8', borderTopLeftRadius: ms(20), borderTopRightRadius: ms(20), paddingTop: ms(20) },
  scrollView: { paddingHorizontal: ms(16), paddingBottom: ms(120) },
  sectionTitle: { fontSize: ms(20, 0.3), fontWeight: 'bold', color: '#1C2C4F', marginBottom: ms(8), marginTop: ms(20) },
  sectionDescription: { fontSize: ms(14, 0.3), color: '#6B7280', marginBottom: ms(12) },
  optionsContainer: { marginBottom: ms(16) },
  optionButton: { backgroundColor: '#FFFFFF', borderRadius: ms(12), padding: ms(12), marginBottom: ms(8), borderWidth: ms(1), borderColor: '#E5E5E5' },
  optionSelected: { borderColor: '#29BFC0', borderWidth: ms(2), backgroundColor: '#E8F8F8' },
  optionText: { fontSize: ms(16, 0.3), color: '#1C2C4F' },
  optionTextSelected: { color: '#29BFC0', fontWeight: '600' },
  inputGroup: { marginBottom: ms(16) },
  label: { fontSize: ms(16, 0.3), fontWeight: '600', color: '#6B7280', marginBottom: ms(8) },
  input: { backgroundColor: '#FFFFFF', borderRadius: ms(25), padding: ms(15), fontSize: ms(16, 0.3), color: '#1C2C4F', borderWidth: ms(1), borderColor: '#d8dbdd' },
  textArea: { minHeight: s(60), textAlignVertical: 'top' },
  checkboxContainer: { flexDirection: 'row', alignItems: 'center', marginTop: ms(20), marginBottom: ms(20) },
  checkbox: { width: s(24), height: s(24), borderRadius: ms(6), borderWidth: ms(2), borderColor: '#E5E5E5', alignItems: 'center', justifyContent: 'center', marginRight: ms(12) },
  checkboxChecked: { backgroundColor: '#29BFC0', borderColor: '#29BFC0' },
  checkmark: { width: s(14), height: s(14), tintColor: '#FFFFFF' },
  checkboxLabel: { fontSize: ms(16, 0.3), color: '#1C2C4F', flex: 1 },
  submitButtonContainer: { alignItems: 'center', marginTop: ms(20), marginBottom: ms(40) },
  saveButton: { backgroundColor: '#29BFC0', borderRadius: ms(25), paddingVertical: ms(15), paddingHorizontal: ms(60), alignItems: 'center', marginBottom: ms(120), minWidth: s(200) },
  saveButtonText: { color: '#FFFFFF', fontSize: ms(16, 0.3), fontWeight: '600' },
  bottomPadding: { height: s(120) },
});
